import React, { useRef, useState } from "react";
import { LuUpload, LuX, LuCheck, LuAlertCircle } from "react-icons/lu";
import i18n from "i18next";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";

const UploadImageTab = ({ uploadFunction, onAddToScene }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [percentage, setPercentage] = useState(0);

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.type.startsWith("image/")) {
      setError(i18n.t("FileModal.APIProviders.error") || "Invalid file type");
      return;
    }

    // 500 MB üst sınırı
    if (isFileTooLarge(selectedFile)) {
      setError(fileTooLargeMessage());
      return;
    }

    setFile(selectedFile);
    setError("");

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    handleFile(selectedFile);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const selectedFile = event.dataTransfer.files?.[0];
    handleFile(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleUpload = () => {
    if (!file || !uploadFunction) return;

    setUploading(true);
    setError("");

    uploadFunction(
      file,
      "",
      (url) => {
        setUploading(false);
        setUploaded(true);
        setUploadedUrl(url);
        if (onAddToScene && url) {
          onAddToScene(url, file.name);
        }
      },
      (percent) => {
        setPercentage(percent);
      }
    );
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setUploading(false);
    setUploaded(false);
    setError("");
    setUploadedUrl(null);
    setPercentage(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="add-image-modal__upload">
      <input
        ref={fileInputRef}
        type="file"
        className="add-image-modal__upload-input"
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      {!file && !uploading && !uploaded && (
        <div
          className={`add-image-modal__upload-dropzone${isDragging ? " add-image-modal__upload-dropzone--active" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="add-image-modal__upload-icon">
            <LuUpload />
          </div>
          <div className="add-image-modal__upload-title">
            {i18n.t("ClickToUploadOrDragDrop") || "Click to upload or drag and drop"}
          </div>
          <div className="add-image-modal__upload-subtitle">
            {i18n.t("PNGJPGOrWEBPMax10MB") || "PNG, JPG, or WEBP (max 100MB)"}
          </div>
        </div>
      )}

      {error && (
        <div
          className="add-image-modal__upload-status add-image-modal__upload-status--error"
          style={{ marginTop: 16 }}
        >
          <LuAlertCircle />
          <span>{error}</span>
        </div>
      )}

      {(file || uploading || uploaded) && (
        <div className="add-image-modal__upload-preview">
          <div className="add-image-modal__upload-preview-image">
            {preview && <img src={preview} alt={file?.name} />}
          </div>

          <div className="add-image-modal__upload-preview-info">
            <div className="add-image-modal__upload-filename">{file?.name}</div>

            {!uploaded && !uploading && (
              <div className="add-image-modal__upload-actions">
                <button
                  type="button"
                  className="add-image-modal__btn add-image-modal__btn--primary"
                  onClick={handleUpload}
                >
                  <LuUpload style={{ marginRight: 6 }} />
                  {i18n.t("FileModal.APIProviders.upload")}
                </button>
                <button
                  type="button"
                  className="add-image-modal__btn add-image-modal__btn--ghost"
                  onClick={handleClear}
                >
                  <LuX style={{ marginRight: 6 }} />
                  {i18n.t("FileModal.APIProviders.clear")}
                </button>
              </div>
            )}

            {uploading && (
              <div className="add-image-modal__upload-progress">
                <div className="add-image-modal__upload-progress-bar">
                  <div
                    className="add-image-modal__upload-progress-fill"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <div className="add-image-modal__upload-progress-text">{percentage}%</div>
              </div>
            )}

            {uploaded && (
              <div className="add-image-modal__upload-status add-image-modal__upload-status--success">
                <LuCheck />
                <span>{i18n.t("FileModal.APIProviders.uploaded")}</span>
              </div>
            )}

            {uploaded && (
              <button
                type="button"
                className="add-image-modal__btn add-image-modal__btn--primary"
                onClick={handleClear}
              >
                {i18n.t("FileModal.APIProviders.UploadNewFile")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadImageTab;
